/* Loads rules (already in the page) and the product feed. Step 1 can render before this finishes. -- SG 20261026 */

import { setProducts, setRules, zeroCandidateTypes } from "./sleep-finder-engine";

const VIEW_PAGE_CAP = 20;
const AJAX_PAGE_CAP = 10;

export interface ConfigWarning {
  reason: string;
  detail?: string;
}

function emitConfigWarning(warning: ConfigWarning): void {
  if (typeof window === "undefined") return;
  const target = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  target.dataLayer = target.dataLayer || [];
  const payload: Record<string, unknown> = { event: "finder_config_warning", reason: warning.reason };
  if (warning.detail) payload.detail = warning.detail;
  target.dataLayer.push(payload);
}

async function readJson(url: string): Promise<any> {
  const response = await fetch(url, { credentials: "same-origin" });
  if (!response.ok) throw new Error("SleepFinder " + response.status);
  return response.json();
}

async function loadView(handle: string, view: string): Promise<{ products: any[]; capped: boolean }> {
  const first = await readJson("/collections/" + handle + "?view=" + encodeURIComponent(view) + "&page=1");
  const reported = Number(first.pages) || 1;
  const pages = Math.min(Math.max(reported, 1), VIEW_PAGE_CAP);
  const capped = reported > VIEW_PAGE_CAP;
  const rest = await Promise.all(
    Array.from({ length: pages - 1 }, (_item, index) =>
      readJson("/collections/" + handle + "?view=" + encodeURIComponent(view) + "&page=" + (index + 2))
    )
  );
  const products = [first, ...rest].flatMap((page) => page.products || []);
  return { products, capped };
}

async function loadAjax(handle: string, pageSize: number): Promise<{ products: any[]; capped: boolean }> {
  const pages = await Promise.all(
    Array.from({ length: AJAX_PAGE_CAP }, (_item, index) =>
      readJson("/collections/" + handle + "/products.json?limit=" + pageSize + "&page=" + (index + 1))
    )
  );
  const products: any[] = [];
  let capped = false;
  for (let index = 0; index < pages.length; index++) {
    const list = pages[index].products || [];
    products.push(...list);
    if (list.length < pageSize) {
      capped = false;
      break;
    }
    if (index === pages.length - 1) capped = true;
  }
  return { products, capped };
}

export async function loadFinderProducts(rules: any, collectionHandle: string): Promise<ConfigWarning[]> {
  setRules(rules);
  const handle = collectionHandle || (rules.settings && rules.settings.collection_handle) || "all";
  const pageSize = (rules.settings && rules.settings.products_json_page_size) || 250;
  const view = rules.settings && rules.settings.data_view;
  const warnings: ConfigWarning[] = [];
  let products: any[] = [];
  let usedFallback = false;
  let capped = false;

  if (view) {
    try {
      const loaded = await loadView(handle, view);
      products = loaded.products;
      capped = loaded.capped;
    } catch (error) {
      usedFallback = true;
      const loaded = await loadAjax(handle, pageSize);
      products = loaded.products;
      capped = loaded.capped;
      console.warn("SleepFinder data view unavailable, using products.json", error);
    }
  } else {
    usedFallback = true;
    const loaded = await loadAjax(handle, pageSize);
    products = loaded.products;
    capped = loaded.capped;
  }

  setProducts(products);

  if (usedFallback) {
    const warning = { reason: "products_json_fallback" };
    warnings.push(warning);
    emitConfigWarning(warning);
  }
  if (capped) {
    const warning = { reason: "page_cap", detail: usedFallback ? "products_json" : "data_view" };
    warnings.push(warning);
    emitConfigWarning(warning);
  }
  const missing = zeroCandidateTypes();
  if (missing.length) {
    const warning = { reason: "zero_candidates", detail: missing.join("|") };
    warnings.push(warning);
    emitConfigWarning(warning);
  }
  return warnings;
}
